import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Alert from '~/components/Alert'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const AlertPage: Component = () => {
  return (
    <>
      <Title>Alert Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4 text-balance">Alert</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Las alertas proporcionan mensajes de retroalimentación contextual para acciones típicas de los usuarios con un puñado de mensajes de respuesta flexibles y comunes.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes Básicas */}
          <Card>
            <Card.Header
              title="Variantes de Color"
              subtitle="Seis estilos de color predefinidos para diferentes propósitos semánticos."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body class="space-y-4">
              <Alert variant="primary">Este es un mensaje de alerta informativo principal.</Alert>
              <Alert variant="secondary">Mensaje de alerta secundario para información adicional.</Alert>
              <Alert variant="success">¡Éxito! La operación se ha completado correctamente.</Alert>
              <Alert variant="danger">Error: Algo salió mal al procesar su solicitud.</Alert>
              <Alert variant="warning">Advertencia: Verifique los datos antes de continuar.</Alert>
              <Alert variant="info">Información: Hay actualizaciones disponibles para el sistema.</Alert>
            </Card.Body>
          </Card>

          {/* Con Títulos e Iconos */}
          <Card>
            <Card.Header
              title="Títulos e Iconos Personalizados"
              subtitle="Mejora la claridad del mensaje añadiendo títulos y utilizando iconos personalizados."
              icon={<Icon icon="material-symbols-light:notifications-active-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body class="space-y-4">
              <Alert variant="primary" title="Nuevas Actualizaciones" icon="material-symbols-light:rocket-launch-outline">
                Hemos añadido nuevas funcionalidades a la plataforma para mejorar tu productividad.
              </Alert>
              <Alert variant="success" title="Pago Confirmado" icon="material-symbols-light:verified-outline">
                Tu suscripción premium ha sido activada con éxito. ¡Bienvenido!
              </Alert>
              <Alert variant="danger" title="Acceso Denegado" icon="material-symbols-light:warning-outline">
                No tienes permisos suficientes para acceder a este módulo. Contacta al administrador.
              </Alert>
              <Alert variant="warning" title="Espacio Insuficiente" icon="material-symbols-light:database-outline">
                Tu almacenamiento está al 95%. Considera ampliar tu plan.
              </Alert>
            </Card.Body>
          </Card>

          {/* Outlined y Closable */}
          <Card>
            <Card.Header
              title="Estilos y Control"
              subtitle="Variantes con borde (outlined) y capacidad de ser cerradas por el usuario."
              icon={<Icon icon="material-symbols-light:settings-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body class="space-y-4">
              <Alert variant="info" outlined title="Modo Lectura">
                El modo lectura está activado para mejorar la visibilidad del contenido.
              </Alert>

              <Alert variant="primary" closable onClose={() => console.log('Alerta cerrada')} title="Mensaje Descartable">
                Esta alerta puede ser cerrada por el usuario haciendo clic en el icono de la derecha.
              </Alert>

              <Alert variant="danger" outlined closable title="Error de Conexión" icon="material-symbols-light:wifi-off">
                Se perdió la conexión con el servidor. Se reintentará automáticamente en 5 segundos.
              </Alert>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades disponibles del componente Alert." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">variant</td>
                      <td class="px-6 py-4 text-foreground-subtle">AlertVariantType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4 text-foreground-muted">Estilo visual de la alerta.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">title</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Título destacado en negrita.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">closable</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Muestra un botón de cierre.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">outlined</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Estilo con borde y fondo sutil.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">icon</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4 font-mono">Auto</td>
                      <td class="px-6 py-4 text-foreground-muted">Icono personalizado (Solar).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">onClose</td>
                      <td class="px-6 py-4 text-foreground-subtle">() =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Callback al cerrar la alerta.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default AlertPage
